/** Cross-surface money refresh: งานเงิน ↔ ข้อมูล ↔ income allocation drawer. */
export const INCOME_MONEY_CHANGED_EVENT = "aera:income-money-changed";

export type IncomeMoneyChangedDetail = {
  reason?: string;
  recordId?: string;
};

/**
 * Reasons that originate on the data tab (or its receivable list).
 * Listeners on ข้อมูล must ignore these to avoid refresh loops.
 */
const DATA_ORIGIN_REASONS = new Set([
  "ledger_refresh",
  "ledger_refresh_soft",
  "money_undone",
  "receivable_pending_changed",
]);

/**
 * Sticky flag: ข้อมูล is unmounted while the user is on งานเงิน, so a live
 * event listener cannot refresh the ledger. Mark pending here; BillinDataView
 * consumes it on remount.
 */
let pendingDataRefresh: IncomeMoneyChangedDetail | null = null;

function shouldMarkDataPending(reason: string): boolean {
  if (DATA_ORIGIN_REASONS.has(reason)) return false;
  // Work-queue reload after close/match, or bare notify from work slip match.
  return reason === "financial_work_reload" || reason === "";
}

export function isDataOriginIncomeMoneyReason(reason?: string): boolean {
  return DATA_ORIGIN_REASONS.has((reason ?? "").trim());
}

/** True when งานเงิน mutated money and ข้อมูล should catch up (incl. after remount). */
export function takePendingDataRefresh(): IncomeMoneyChangedDetail | null {
  const next = pendingDataRefresh;
  pendingDataRefresh = null;
  return next;
}

/** Test/helper: peek without clearing. */
export function peekPendingDataRefresh(): IncomeMoneyChangedDetail | null {
  return pendingDataRefresh;
}

export function markDataRefreshPending(
  detail?: IncomeMoneyChangedDetail,
): void {
  pendingDataRefresh = {
    reason: "financial_work_reload",
    ...detail,
  };
}

export function notifyIncomeMoneyChanged(
  detail?: IncomeMoneyChangedDetail,
): void {
  const d = detail ?? {};
  const reason = (d.reason ?? "").trim();
  if (shouldMarkDataPending(reason)) {
    markDataRefreshPending({
      ...d,
      reason: reason || "financial_work_money_changed",
    });
  }
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(INCOME_MONEY_CHANGED_EVENT, { detail: d }),
  );
}
